import { LoadingSpinner } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack, useTheme } from "@mui/material";
import { useGetAgreements } from "@src/appV2/Agreements/api/useGetAgreements";
import { AgreementKind } from "@src/appV2/Agreements/types";
import { getNewestSignedAgreementFromResponse } from "@src/appV2/Agreements/utils/getNewestSignedAgreementFromResponse";
import { ErrorPageTemplate } from "@src/appV2/Errors/Template";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS, ClipboardLogo, useToast } from "@src/appV2/lib";
import { logError } from "@src/appV2/lib/analytics/log";
import { useLogErrorEffect } from "@src/appV2/lib/analytics/useLogErrorEffect";
import { PdfViewerProvider } from "@src/appV2/PdfViewer/context";
import { Button } from "@src/appV2/redesign/components/Button";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";

import { useCreatePaymentServiceAccount } from "../../api/useCreatePaymentServiceAccount";
import { type OnboardingStepProps } from "../OnboardingStepWrapper";
import { ICAgreementStepContainer } from "./ICAgreementStepContainer";

export function ICAgreementStepContent(props: OnboardingStepProps) {
  const { worker, onNext } = props;

  const workerId = worker?.userId ?? "";
  const theme = useTheme();
  const { showErrorToast } = useToast();

  const { data: agreements, status: agreementsStatus } = useGetAgreements(
    {
      workerId,
      kind: [AgreementKind.INDEPENDENT_CONTRACTOR],
    },
    { refetchOnWindowFocus: false, enabled: Boolean(workerId) }
  );

  const { mutateAsync: createPaymentServiceAccount } = useCreatePaymentServiceAccount();
  const isNewStripeOnboardingEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_NEW_STRIPE_ONBOARDING, {
    defaultValue: false,
  });

  // Should never happen, but just adding for events for safety
  const isWorkerUndefined = !isDefined(worker);
  useLogErrorEffect(
    APP_V2_APP_EVENTS.GET_AGREEMENTS_FAILURE,
    {
      error: new Error("Worker is undefined"),
    },
    {
      enabled: isWorkerUndefined,
    }
  );

  if (agreementsStatus === "loading") {
    return (
      <PageContent>
        <Stack spacing={3} alignItems="center" justifyContent="center" height="100%" width="100%">
          <LoadingSpinner size="large" />
        </Stack>
      </PageContent>
    );
  }

  const agreementToSign =
    agreementsStatus === "success" ? getNewestSignedAgreementFromResponse(agreements) : undefined;

  const pdfUrl = agreementToSign?.attributes.downloadUrl;

  if (isWorkerUndefined || !isDefined(agreementToSign) || agreementsStatus === "error") {
    return (
      <ErrorPageTemplate
        illustration={<ClipboardLogo />}
        title="Oops! Something went wrong."
        description="We're sorry, but something went wrong. Please try again or contact support if the problem persists."
        sx={{
          backgroundColor: theme.palette.background.tertiary,
        }}
        actions={
          <Stack width="100%">
            <Button
              fullWidth
              variant="brand"
              size="large"
              onClick={() => {
                globalThis.location.reload();
              }}
            >
              Reload page
            </Button>
          </Stack>
        }
      />
    );
  }

  const createPaymentAccountIfNotExists = async () => {
    if (!worker.paymentAccountInfo) {
      // Used same attributes as we sent in old sign up flow.
      await createPaymentServiceAccount({
        agentId: worker.userId,
        email: worker.email,
        msa: worker.address?.metropolitanStatisticalArea,
        individual: {
          firstName: worker.firstName,
          lastName: worker.lastName,
          phone: worker.phone,
          birthday: worker.dob,
          address: {
            line1: `${worker.address?.streetNumber ?? ""} ${worker.address?.streetName ?? ""}`,
            city: worker.address?.city,
            state: worker.address?.stateCode,
            postalCode: worker.address?.postalCode,
          },
        },
        isOnboardingCompleted: isNewStripeOnboardingEnabled ? false : undefined,
      }).catch((error) => {
        // We don't want to block user in case of error, so we just show a toast and log the error.
        showErrorToast(
          "You have accepted the Independent Contractor Agreement successfully. However, we are having a technical issue accessing or creating your Stripe account.  Please contact Customer Support."
        );
        logError(APP_V2_APP_EVENTS.STRIPE_ONBOARDING_ERROR, {
          error,
          metadata: {
            message: "Error occurred setting up stripe account on IC Agreement signing",
            userId: worker.userId,
          },
        });
      });
    }
  };

  return (
    <PdfViewerProvider fileName="clipboard_health_contract.pdf" pdfUrl={pdfUrl}>
      <ICAgreementStepContainer
        agreementsStatus={agreementsStatus}
        agreementToSign={agreementToSign}
        onAcceptAgreement={async () => {
          await createPaymentAccountIfNotExists();
          await onNext();
        }}
      />
    </PdfViewerProvider>
  );
}
